import React from "react";
import { useLocation } from "react-router-dom";

import { faEllipsisH } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	AvatarGroup,
	AvatarProfile,
	Chip,
	DropDownMenu,
	DropDownMenuOption,
	type ICellRendererParams,
	Icon,
	Link,
	type PYColumnDef,
	type SortChangedEvent,
	Table,
	Text,
	TextDate,
} from "@performyard/py-components";
import type { ExtendedWorkspace } from "@performyard/service-contracts";

import { toAvatarGroupEmployees } from "../../utils/participants";

interface WorkspacesTableProps {
	workspaces: ExtendedWorkspace[];
	isFetchingWorkspaces: boolean;
	activeSort: string;
	activeSortDirection: string;
	handleColumnHeaderClick: (column: { name: string; direction: "asc" | "desc" }) => void;
	userId: string;
	handleEditWorkspace: (workspace: ExtendedWorkspace) => void;
	handleWorkspaceStatusChange: (workspaceId: string, status: "active" | "archived") => void;
}

const WorkspacesTable = (props: WorkspacesTableProps) => {
	const {
		workspaces,
		isFetchingWorkspaces,
		activeSort,
		activeSortDirection,
		handleColumnHeaderClick,
		userId,
		handleEditWorkspace,
		handleWorkspaceStatusChange,
	} = props;

	const location = useLocation();
	const currentSearchParams = location.search;

	const columnDefs: PYColumnDef<ExtendedWorkspace>[] = [
		{
			field: "name",
			headerName: "Meeting Workspace Name",
			sortable: true,
			resizable: false,
			width: 380,
			minWidth: 300,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ExtendedWorkspace>) => {
				const workspace = params.data;
				if (!workspace) return null;
				const href = currentSearchParams
					? `/meeting-workspaces/${workspace._id}?from=${encodeURIComponent(currentSearchParams)}`
					: `/meeting-workspaces/${workspace._id}`;
				return (
					<div className="flex h-full w-full min-w-0 items-center">
						<Link href={href}>
							<Text
								size="paragraph-3"
								weight="regular"
								dangerouslySetClassName="py-line-clamp-1 w-full min-w-0 max-w-full"
							>
								{workspace.name}
							</Text>
						</Link>
					</div>
				);
			},
		},
		{
			field: "status",
			headerName: "Status",
			sortable: true,
			resizable: false,
			width: 104,
			minWidth: 104,
			maxWidth: 104,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ExtendedWorkspace>) => {
				const workspace = params.data;
				if (!workspace) return null;
				let statusChip: React.ReactNode = null;
				if (workspace.status === "active") {
					statusChip = <Chip label="Active" color="primary" size={24} />;
				}
				if (workspace.status === "archived") {
					statusChip = <Chip label="Archived" color="neutral" size={24} />;
				}
				return <div className="flex h-full w-full min-w-0 items-center">{statusChip}</div>;
			},
		},
		{
			field: "creator",
			headerName: "Creator",
			sortable: true,
			resizable: false,
			width: 148,
			minWidth: 148,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ExtendedWorkspace>) => {
				const workspace = params.data;
				if (!workspace) return null;
				if (workspace.created?.userId && workspace.created?.name) {
					return (
						<div className="flex h-full w-full min-w-0 items-center">
							<AvatarProfile
								employeeId={workspace.created.userId}
								employeeName={workspace.created.name}
							/>
						</div>
					);
				}
				return null;
			},
		},
		{
			field: "participants",
			headerName: "Participants",
			sortable: false,
			resizable: false,
			width: 116,
			minWidth: 116,
			maxWidth: 116,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ExtendedWorkspace>) => {
				const workspace = params.data;
				if (!workspace) return null;
				const employees = toAvatarGroupEmployees(workspace.participants, { currentUserId: userId });
				return (
					<div className="flex h-full w-full min-w-0 items-center">
						<AvatarGroup
							employees={employees}
							hasLinks={true}
							maxLength={employees.length <= 5 ? 5 : 4}
							employeeNameTooltip={true}
							modalTitle="Workspace Participants"
							modalSubtitle={workspace.name}
						/>
					</div>
				);
			},
		},
		{
			field: "previousMeeting",
			headerName: "Previous Meeting",
			sortable: true,
			resizable: false,
			width: 132,
			minWidth: 132,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ExtendedWorkspace>) => {
				const workspace = params.data;
				if (!workspace?.previousMeetingStartDateTime) return null;
				return (
					<div className="flex h-full w-full min-w-0 items-center">
						<TextDate date={workspace.previousMeetingStartDateTime} hasTime={false} dateSize="medium" />
					</div>
				);
			},
		},
		{
			field: "upcomingMeeting",
			headerName: "Upcoming Meeting",
			sortable: true,
			resizable: false,
			width: 144,
			minWidth: 144,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ExtendedWorkspace>) => {
				const workspace = params.data;
				if (!workspace?.upcomingMeetingStartDateTime) return null;
				return (
					<div className="flex h-full w-full min-w-0 items-center">
						<TextDate date={workspace.upcomingMeetingStartDateTime} hasTime={false} dateSize="medium" />
					</div>
				);
			},
		},
		{
			field: "control",
			headerName: "",
			sortable: false,
			resizable: false,
			width: 56,
			minWidth: 56,
			maxWidth: 56,
			cellClass: "flex items-center",
			cellRenderer: (params: ICellRendererParams<ExtendedWorkspace>) => {
				const workspace = params.data;
				if (!workspace) return null;
				if (workspace.created.userId === userId) {
					const options: DropDownMenuOption[] = [
						{ key: "edit-key", label: "Edit", onClick: () => handleEditWorkspace(workspace) },
					];
					if (workspace.status === "active")
						options.push({
							key: "archive-key",
							label: "Archive",
							onClick: () => handleWorkspaceStatusChange(workspace._id, "archived"),
						});
					if (workspace.status === "archived")
						options.push({
							key: "activate-key",
							label: "Activate",
							onClick: () => handleWorkspaceStatusChange(workspace._id, "active"),
						});
					return (
						<div className="flex h-full w-full min-w-0 items-center justify-center">
							<DropDownMenu
								options={options}
								placement="bottom-end"
								className="group flex h-full items-center"
							>
								<Icon
									icon={faEllipsisH}
									size={24}
									color="neutral-7"
									dangerouslySetClassName="transition-colors group-hover:text-neutral-9"
								/>
							</DropDownMenu>
						</div>
					);
				}
				return <div className="h-8" />;
			},
		},
	];

	const onSortChanged = (event: SortChangedEvent<ExtendedWorkspace>) => {
		const sortedColumn = event.api.getColumnState().find((col) => col.sort);
		if (sortedColumn?.colId) {
			handleColumnHeaderClick({
				name: sortedColumn.colId,
				direction: sortedColumn.sort as "asc" | "desc",
			});
		}
	};

	return (
		<div data-testid="workspacesContainer">
			<Table<ExtendedWorkspace>
				autoHeight
				columnSizing="fit-grid"
				rowData={workspaces}
				columnDefs={columnDefs}
				loading={isFetchingWorkspaces}
				getRowId={(params) => params.data._id}
				onSortChanged={onSortChanged}
				emptyStateText="No Meeting Workspaces Found"
				initialState={{
					sort: {
						sortModel: activeSort
							? [{ colId: activeSort, sort: activeSortDirection as "asc" | "desc" }]
							: [],
					},
				}}
				animateRows={false}
			/>
		</div>
	);
};

export default WorkspacesTable;
